/**
 * Shift context (Phase P2h — multi-department support).
 *
 * Umożliwia użytkownikom z wieloma dozwolonymi działami wybór działu, na którym
 * pracują "dziś". Wybór jest utrwalany w localStorage (per session).
 *
 * Zasady:
 * - Owner/Manager/Accounting/Readonly (allowed_departments === null) → brak pickera,
 *   bo mają pełen dostęp do wszystkich działów.
 * - Line staff z pojedynczym działem (allowed_departments.length === 1)
 *   → brak pickera, aktywny dział = jedyny dostępny.
 * - Line staff z wieloma działami → picker wymagany, wybór trafia do localStorage.
 *
 * Hook `useActiveShift()` zwraca { activeShift, setActiveShift, needsShiftPicker }.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";

const ShiftContext = createContext(null);

const STORAGE_KEY = "tt_active_shift";

export function ShiftProvider({ children }) {
  const { user } = useAuth();
  const [activeShift, setActiveShiftState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  // Jeśli user ma pojedynczy dział (allowed_departments.length === 1)
  // → auto-ustaw ten dział jako aktywny.
  useEffect(() => {
    if (!user) return;
    const allowed = user.allowed_departments;
    if (Array.isArray(allowed) && allowed.length === 1) {
      if (activeShift !== allowed[0]) {
        setActiveShiftState(allowed[0]);
        try { localStorage.setItem(STORAGE_KEY, allowed[0]); }
        catch (err) { console.warn("[shift] setItem failed:", err); }
      }
    }
    // Jeśli zapisany shift NIE jest już w allowed_departments (np. po odebraniu uprawnień),
    // wyczyść — user zostanie zapytany ponownie.
    if (Array.isArray(allowed) && allowed.length > 0 && activeShift && !allowed.includes(activeShift)) {
      setActiveShiftState(null);
      try { localStorage.removeItem(STORAGE_KEY); }
      catch (err) { console.warn("[shift] removeItem failed:", err); }
    }
  }, [user, activeShift]);

  const setActiveShift = useCallback((dept) => {
    setActiveShiftState(dept);
    try {
      if (dept) localStorage.setItem(STORAGE_KEY, dept);
      else localStorage.removeItem(STORAGE_KEY);
    } catch (err) { console.warn("[shift] setActiveShift persist failed:", err); }
  }, []);

  // Czy user powinien zobaczyć picker (multi-dept, jeszcze nie wybrał)?
  const allowed = user?.allowed_departments;
  const isMultiDept = Array.isArray(allowed) && allowed.length > 1;
  const needsShiftPicker = isMultiDept && !activeShift;

  const value = {
    activeShift,        // string | null — "SALA", "BAR", "MASAZ" lub null
    setActiveShift,     // (dept: string | null) => void
    needsShiftPicker,   // boolean — czy pokazać modal wyboru?
    isMultiDept,        // boolean — czy user ma > 1 dział?
    allowedDepartments: allowed,  // array lub null|undefined dla full-access userów
  };

  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
}

export function useActiveShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error("useActiveShift must be inside ShiftProvider");
  return ctx;
}
